import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchProjects, getProjectsSync } from "@/services/api";
import { useApiData } from "@/hooks/useApiData";

const FeaturedProjects = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [direction, setDirection] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const timerRef = useRef(null);
  const loadedImages = useRef(new Set());

  const { data } = useApiData(() => fetchProjects(), [], getProjectsSync());
  const projects = data || [];
  const total = projects.length;

  const go = useCallback(
    (dir) => {
      if (total === 0) return;
      setDirection(dir);
      setCurrent((prev) => (prev + dir + total) % total);
    },
    [total],
  );

  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (total === 0) return;
    timerRef.current = setInterval(() => go(1), 10000);
  }, [go, total]);

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, [resetTimer]);

  const project = total > 0 ? projects[current % total] : null;

  useEffect(() => {
    if (project && loadedImages.current.has(project.coverImage)) {
      setImgLoaded(true);
    } else {
      setImgLoaded(false);
    }
  }, [project]);

  const handlePrev = () => {
    go(-1);
    resetTimer();
  };
  const handleNext = () => {
    go(1);
    resetTimer();
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
  };

  if (!project) {
    return (
      <section className="py-24 bg-[#050505]" data-testid="featured-projects-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase mb-4">PROJELERİMİZ</p>
            <h2 className="font-light text-4xl md:text-5xl tracking-tight text-[#F5F5F7] mb-4">Tüm Projeler</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8 animate-pulse">
            <div className="relative w-full aspect-video bg-[#111111]" />
            <div className="flex flex-col justify-center py-8 md:py-0 gap-4">
              <div className="h-3 w-32 bg-[#1A1A1A] rounded" />
              <div className="h-9 w-56 bg-[#1A1A1A] rounded" />
              <div className="h-16 w-full bg-[#1A1A1A] rounded" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-14 md:py-24 bg-[#050505]"
      data-testid="featured-projects-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Başlık */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase mb-4">
            PROJELERİMİZ
          </p>
          <h2 className="font-light text-4xl md:text-5xl tracking-tight text-[#F5F5F7] mb-4">
            Tüm Projeler
          </h2>
          <p className="text-[#86868B] text-base leading-relaxed max-w-xl mx-auto">
            Konya'nın en prestijli lokasyonlarında gerçekleştirdiğimiz
            projelerle yaşam kalitesini yeniden tanımlıyoruz.
          </p>
        </motion.div>

        {/* Carousel */}
        <div
          className="relative overflow-hidden"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={current}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8"
            >
              {/* Resim */}
              <div className="relative w-full aspect-video overflow-hidden">
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] pointer-events-none transition-opacity duration-700 ${
                    imgLoaded ? 'opacity-0' : 'animate-pulse'
                  }`}
                />
                <img
                  src={project.coverImage}
                  alt={`Ober Yapı ${project.title} Konya ${project.type || ""} Projesi`}
                  className={`w-full h-full object-cover transition-opacity duration-700 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
                  fetchpriority={current === 0 ? "high" : "auto"}
                  onLoad={() => {
                    if (project) loadedImages.current.add(project.coverImage);
                    setImgLoaded(true);
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                {/* Durum etiketi */}
                <div className="absolute top-4 left-4">
                  <span
                    className={`text-xs font-medium tracking-widest uppercase px-3 py-1 ${
                      project.status === "completed"
                        ? "bg-[#C5A059]/90 text-black"
                        : project.status === "ongoing"
                          ? "bg-[#F5F5F7]/90 text-black"
                          : "bg-[#333]/90 text-[#F5F5F7]"
                    }`}
                  >
                    {project.statusLabel}
                  </span>
                </div>
              </div>

              {/* İçerik */}
              <div className="flex flex-col justify-center py-8 md:py-0">
                <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase mb-3">
                  {project.location} · {project.year}
                </p>
                <h3 className="text-[#F5F5F7] font-light text-3xl md:text-4xl tracking-tight mb-4">
                  {project.title}
                </h3>
                {project.shortDescription && (
                  <p className="text-[#86868B] text-sm leading-relaxed mb-6 max-w-md">
                    {project.shortDescription}
                  </p>
                )}
                <div className="flex gap-6 mb-8 text-sm text-[#86868B]">
                  <span>{project.units}</span>
                  <span className="text-[#C5A059]/40">|</span>
                  <span>{project.area}</span>
                  <span className="text-[#C5A059]/40">|</span>
                  <span>{project.type}</span>
                </div>
                <button
                  onClick={() =>
                    navigate(`/projeler/${project.slug || project.id}`)
                  }
                  className="self-start bg-[#C5A059] hover:bg-[#D4AF37] text-black px-6 py-3 text-xs font-semibold tracking-widest uppercase transition-colors duration-200"
                >
                  Projeyi İncele
                </button>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Prev / Next — hover'da göster */}
          <AnimatePresence>
            {hovered && (
              <>
                <motion.button
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.2 }}
                  onClick={handlePrev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/60 hover:bg-[#C5A059] text-white hover:text-black transition-colors duration-200"
                  aria-label="Önceki proje"
                >
                  <ChevronLeft size={20} />
                </motion.button>
                <motion.button
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.2 }}
                  onClick={handleNext}
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/60 hover:bg-[#C5A059] text-white hover:text-black transition-colors duration-200"
                  aria-label="Sonraki proje"
                >
                  <ChevronRight size={20} />
                </motion.button>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Dot indikatörler */}
        <div className="flex justify-center gap-2 mt-8">
          {projects.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > current ? 1 : -1);
                setCurrent(i);
                resetTimer();
              }}
              className="transition-all duration-300"
              style={{
                width: i === current ? 28 : 8,
                height: 4,
                backgroundColor:
                  i === current ? "#C5A059" : "rgba(255,255,255,0.2)",
              }}
              aria-label={`${i + 1}. proje`}
            />
          ))}
        </div>

        {/* Tüm projelere git */}
        <div className="text-center mt-10">
          <button
            onClick={() => navigate("/projeler")}
            className="border border-white/20 hover:border-[#C5A059] text-[#F5F5F7] hover:text-[#C5A059] px-8 py-3 text-xs font-semibold tracking-widest uppercase transition-all duration-200"
            data-testid="view-all-projects-button"
          >
            Tüm Projeleri Görüntüle
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
